/**
 * Resistor colour code (IEC 60062:2016) and the E-series of preferred values (IEC 60063).
 *
 * Decoding turns the printed bands into a resistance, tolerance and temperature
 * coefficient; encoding does the reverse for a chosen number of bands; the E-series
 * helpers find the standard value nearest a calculated one.
 */

export type ColorId =
  | "black"
  | "brown"
  | "red"
  | "orange"
  | "yellow"
  | "green"
  | "blue"
  | "violet"
  | "grey"
  | "white"
  | "gold"
  | "silver";

export interface ColorDef {
  id: ColorId;
  /** Paint colour for the drawing. */
  hex: string;
  /** Digit value, for the ten digit colours. */
  digit?: number;
  /** Multiplier as a power of ten. */
  multExp: number;
  /** Tolerance in percent, where the colour is a tolerance band. */
  tolerance?: number;
  /** Temperature coefficient in ppm/K, where the colour is a sixth band. */
  tempco?: number;
}

export const COLORS: readonly ColorDef[] = [
  { id: "black", hex: "#111111", digit: 0, multExp: 0, tempco: 250 },
  {
    id: "brown",
    hex: "#7a3e12",
    digit: 1,
    multExp: 1,
    tolerance: 1,
    tempco: 100,
  },
  { id: "red", hex: "#d62828", digit: 2, multExp: 2, tolerance: 2, tempco: 50 },
  { id: "orange", hex: "#f77f00", digit: 3, multExp: 3, tempco: 15 },
  { id: "yellow", hex: "#f4d000", digit: 4, multExp: 4, tempco: 25 },
  {
    id: "green",
    hex: "#2a9d4a",
    digit: 5,
    multExp: 5,
    tolerance: 0.5,
    tempco: 20,
  },
  {
    id: "blue",
    hex: "#1d4ed8",
    digit: 6,
    multExp: 6,
    tolerance: 0.25,
    tempco: 10,
  },
  {
    id: "violet",
    hex: "#7e22ce",
    digit: 7,
    multExp: 7,
    tolerance: 0.1,
    tempco: 5,
  },
  {
    id: "grey",
    hex: "#8d8d8d",
    digit: 8,
    multExp: 8,
    tolerance: 0.05,
    tempco: 1,
  },
  { id: "white", hex: "#f7f7f7", digit: 9, multExp: 9 },
  { id: "gold", hex: "#d4af37", multExp: -1, tolerance: 5 },
  { id: "silver", hex: "#c4c4c4", multExp: -2, tolerance: 10 },
];

export const DIGIT_COLORS: readonly ColorId[] = COLORS.filter(
  (c) => c.digit !== undefined
).map((c) => c.id);
export const TOLERANCE_COLORS: readonly ColorId[] = COLORS.filter(
  (c) => c.tolerance !== undefined
).map((c) => c.id);
export const TEMPCO_COLORS: readonly ColorId[] = COLORS.filter(
  (c) => c.tempco !== undefined
).map((c) => c.id);

/** No tolerance band: a three-band resistor, ±20%. */
export const NO_BAND = "none";
export const NO_BAND_TOLERANCE = 20;

export type BandCount = 4 | 5 | 6;

export function colorDef(id: ColorId): ColorDef {
  return COLORS.find((c) => c.id === id) ?? COLORS[0];
}

/** Exact decimal shift: Number("470e-1") has no float noise that 470 * 10 ** -1 may add. */
function scaled(digits: number, exp: number): number {
  return Number(`${digits}e${exp}`);
}

export interface DecodedResistor {
  ohms: number;
  /** Tolerance in percent. */
  tolerance: number;
  min: number;
  max: number;
  /** ppm/K, only for a six-band resistor. */
  tempco: number | null;
  /** The digits as one number, before the multiplier. */
  significand: number;
  multExp: number;
}

export interface BandSet {
  digits: readonly ColorId[];
  multiplier: ColorId;
  tolerance: ColorId | typeof NO_BAND;
  tempco?: ColorId;
}

/** Read the bands as the standard defines them. */
export function decodeBands(bands: BandSet): DecodedResistor {
  const significand = Number(
    bands.digits.map((id) => String(colorDef(id).digit ?? 0)).join("")
  );
  const multExp = colorDef(bands.multiplier).multExp;
  const ohms = scaled(significand, multExp);
  const tolerance =
    bands.tolerance === NO_BAND
      ? NO_BAND_TOLERANCE
      : (colorDef(bands.tolerance).tolerance ?? NO_BAND_TOLERANCE);
  const tempco =
    bands.tempco === undefined ? null : (colorDef(bands.tempco).tempco ?? null);
  return {
    ohms,
    tolerance,
    min: ohms * (1 - tolerance / 100),
    max: ohms * (1 + tolerance / 100),
    tempco,
    significand,
    multExp,
  };
}

export type EncodeError = "not-positive" | "too-many-digits" | "out-of-range";

export type EncodeResult =
  | { ok: true; bands: BandSet; significand: number; multExp: number }
  | { ok: false; error: EncodeError };

/** The tolerance colour for a percentage, or null when no band marks it. */
export function toleranceColor(
  percent: number
): ColorId | typeof NO_BAND | null {
  if (percent === NO_BAND_TOLERANCE) {
    return NO_BAND;
  }
  return COLORS.find((c) => c.tolerance === percent)?.id ?? null;
}

/** The ppm/K tolerance values a sixth band can carry, as colour ids. */
export function tempcoColor(ppm: number): ColorId | null {
  return COLORS.find((c) => c.tempco === ppm)?.id ?? null;
}

function colorForDigit(digit: number): ColorId {
  return COLORS.find((c) => c.digit === digit)?.id ?? "black";
}

function colorForMultiplier(exp: number): ColorId | null {
  return COLORS.find((c) => c.multExp === exp)?.id ?? null;
}

/**
 * Bands for a value: two significant digits on a four-band resistor, three on a
 * five- or six-band one. A value that needs more digits than the bands carry is an
 * error, not a rounded answer — the colours would then mean a different resistor.
 */
export function encodeResistance(
  ohms: number,
  bandCount: BandCount,
  tolerance: ColorId | typeof NO_BAND,
  tempco?: ColorId
): EncodeResult {
  if (!(Number.isFinite(ohms) && ohms > 0)) {
    return { ok: false, error: "not-positive" };
  }
  const capacity = bandCount === 4 ? 2 : 3;
  const [mantissa, power] = ohms.toExponential(11).split("e");
  const mantissaDigits = mantissa.replace(".", "").replace(TRAILING_ZEROS, "");
  if (mantissaDigits.length > capacity) {
    return { ok: false, error: "too-many-digits" };
  }
  const digits = mantissaDigits.padEnd(capacity, "0");
  const multExp = Number(power) - (capacity - 1);
  const multiplier = colorForMultiplier(multExp);
  if (multiplier === null) {
    return { ok: false, error: "out-of-range" };
  }
  return {
    ok: true,
    significand: Number(digits),
    multExp,
    bands: {
      digits: [...digits].map((d) => colorForDigit(Number(d))),
      multiplier,
      tolerance,
      tempco: bandCount === 6 ? (tempco ?? "brown") : undefined,
    },
  };
}

export type SeriesId = "E6" | "E12" | "E24" | "E48" | "E96" | "E192";

/** Three-digit significands (100–999) of each E-series decade. */
function computedSeries(count: number, overrides: Record<number, number> = {}) {
  return Array.from(
    { length: count },
    (_, k) => overrides[k] ?? Math.round(10 ** (k / count) * 100)
  );
}

export const E_SERIES: Record<SeriesId, readonly number[]> = {
  E6: [100, 150, 220, 330, 470, 680],
  E12: [100, 120, 150, 180, 220, 270, 330, 390, 470, 560, 680, 820],
  E24: [
    100, 110, 120, 130, 150, 160, 180, 200, 220, 240, 270, 300, 330, 360, 390,
    430, 470, 510, 560, 620, 680, 750, 820, 910,
  ],
  E48: computedSeries(48),
  E96: computedSeries(96),
  // IEC 60063 prints 9.20 where the formula gives 9.19.
  E192: computedSeries(192, { 185: 920 }),
};

export const SERIES_IDS = Object.keys(E_SERIES) as SeriesId[];

/** The tolerance each series is normally paired with, in percent. */
export const SERIES_TOLERANCE: Record<SeriesId, number> = {
  E6: 20,
  E12: 10,
  E24: 5,
  E48: 2,
  E96: 1,
  E192: 0.5,
};

const ALL_ZEROS = /^0*$/;
const TRAILING_ZEROS = /0+$/;

/**
 * The value's first three digits as 100–999 (4700 → 470). A value with more than three
 * significant digits gets a half added, so it matches no series entry.
 */
export function significandOf(value: number): number {
  const mantissa = value.toExponential(11).split("e")[0].replace(".", "");
  const head = Number(mantissa.slice(0, 3));
  return ALL_ZEROS.test(mantissa.slice(3)) ? head : head + 0.5;
}

/** Series that contain this exact value. */
export function seriesContaining(value: number): SeriesId[] {
  if (!(Number.isFinite(value) && value > 0)) {
    return [];
  }
  const significand = significandOf(value);
  return SERIES_IDS.filter((id) => E_SERIES[id].includes(significand));
}

export interface NearestValue {
  nearest: number;
  /** Largest series value at or below the input, and smallest at or above it. */
  below: number;
  above: number;
  /** Signed percentage from the input to the nearest value. */
  errorPercent: number;
}

function seriesValues(series: SeriesId, decade: number): number[] {
  return E_SERIES[series].map((s) => scaled(s, decade - 2));
}

/** The standard value closest to `ohms` (by ratio), with its neighbours on each side. */
export function nearestStandard(
  ohms: number,
  series: SeriesId
): NearestValue | null {
  if (!(Number.isFinite(ohms) && ohms > 0)) {
    return null;
  }
  const decade = Math.floor(Math.log10(ohms));
  const pool = [decade - 1, decade, decade + 1].flatMap((d) =>
    seriesValues(series, d)
  );
  const below = Math.max(...pool.filter((v) => v <= ohms * (1 + 1e-12)));
  const above = Math.min(...pool.filter((v) => v >= ohms * (1 - 1e-12)));
  const nearest =
    Math.abs(Math.log(ohms / below)) <= Math.abs(Math.log(above / ohms))
      ? below
      : above;
  return {
    nearest,
    below,
    above,
    errorPercent: ((nearest - ohms) / ohms) * 100,
  };
}

/** The six colour choices of the "read the bands" form, before a band count trims them. */
export interface PaintedBands {
  d1: ColorId;
  d2: ColorId;
  d3: ColorId;
  mult: ColorId;
  tol: ColorId | typeof NO_BAND;
  tc: ColorId;
}

/** The bands a resistor of `count` bands shows for the form's choices. */
export function bandSetFrom(painted: PaintedBands, count: BandCount): BandSet {
  return {
    digits:
      count === 4
        ? [painted.d1, painted.d2]
        : [painted.d1, painted.d2, painted.d3],
    multiplier: painted.mult,
    tolerance: count === 4 || painted.tol !== NO_BAND ? painted.tol : "brown",
    tempco: count === 6 ? painted.tc : undefined,
  };
}

/** Encode with a tolerance in percent and a ppm/K value; a missing band is ±20%. */
export function encodeForForm(
  ohms: number,
  count: BandCount,
  tolerancePercent: number,
  tempcoPpm: number
): EncodeResult {
  const tolerance = toleranceColor(tolerancePercent) ?? "gold";
  return encodeResistance(
    ohms,
    count,
    count === 4 || tolerance !== NO_BAND ? tolerance : "brown",
    tempcoColor(tempcoPpm) ?? "brown"
  );
}

/**
 * Bands for a standard value: four bands when two digits are enough (E6–E24), five
 * otherwise, with the tolerance each series is usually sold at.
 */
export function bandsForStandard(ohms: number, series: SeriesId): EncodeResult {
  const percent = SERIES_TOLERANCE[series];
  const four = encodeForForm(ohms, 4, percent, 100);
  return four.ok ? four : encodeForForm(ohms, 5, percent, 100);
}
